import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { aplicarErrosDoServidor } from '@/api/erros';
import type {
  MedicamentoDoTratamentoResponse,
  RegistrarVigenciaDtoFrequencia,
  RegistrarVigenciaDtoVia,
} from '@/api/generated/model';
import {
  getTratamentoVigenteControllerTratamentoVigenteQueryKey,
  getTratamentosControllerListarQueryKey,
  getTratamentosControllerObterQueryKey,
  useTratamentosControllerRegistrarVigencia,
} from '@/api/generated/tratamentos/tratamentos';
import {
  descreverDose,
  descreverFrequencia,
  formatarDia,
  FREQUENCIAS,
  numeroParaCampo,
  paraNumero,
  ROTULO_FREQUENCIA,
  ROTULO_VIA,
  sugerirDosePorKg,
  VIAS,
} from '@/features/historico/tratamento';
import { Campo, CLASSE_CONTROLE } from '@/shared/components/Campo';
import { Alert, AlertDescription } from '@/shared/ui/alert';
import { Button } from '@/shared/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import { Input } from '@/shared/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select';
import { toast } from '@/shared/ui/sonner';

const VIA_NENHUMA = 'NENHUMA';
const DECIMAL = /^\d{1,9}([.,]\d{1,4})?$/;

const schema = z.object({
  vigenteDe: z.string().min(1, 'Informe a partir de quando a nova dose vale.'),
  doseValor: z
    .string()
    .trim()
    .refine((v) => DECIMAL.test(v) && paraNumero(v) > 0, 'Informe a dose'),
  doseUnidade: z.string().trim().min(1, 'Informe a unidade').max(20),
  dosePorKg: z
    .string()
    .trim()
    .refine((v) => v === '' || (DECIMAL.test(v) && paraNumero(v) > 0), 'Dose/kg inválida'),
  frequencia: z.string().min(1),
  frequenciaLivre: z.string().trim().max(120),
  via: z.string(),
  motivo: z.string().trim().max(500),
});

type Formulario = z.input<typeof schema>;

interface Props {
  aberto: boolean;
  aoFechar: () => void;
  tratamentoId: string;
  pacienteId: string;
  medicamento: MedicamentoDoTratamentoResponse;
  pesoKg: number | null;
}

/** Hoje em `AAAA-MM-DD`, no calendário de quem está na frente da tela. */
function hoje(): string {
  const agora = new Date();
  const ajustado = new Date(agora.getTime() - agora.getTimezoneOffset() * 60_000);
  return ajustado.toISOString().slice(0, 10);
}

/**
 * Ajuste de dose avulso — o que se faz fora da consulta (telefonema do tutor,
 * resultado que chegou depois).
 *
 * 🔴 **Ele INSERE uma vigência nova; nunca edita a anterior.** A dose que valia
 * até ontem continua no histórico com o período dela fechado — é isso que a
 * tabela da aba prova, e é isso que permite à Análise Comparativa dizer "este
 * exame foi colhido com o animal em 1 mg/kg".
 */
export function AjusteDeDoseDialogo({
  aberto,
  aoFechar,
  tratamentoId,
  pacienteId,
  medicamento,
  pesoKg,
}: Props) {
  const queryClient = useQueryClient();
  const registrar = useTratamentosControllerRegistrarVigencia();
  const enviando = useRef(false);

  const atual = medicamento.doseVigente;

  const form = useForm<Formulario>({ resolver: zodResolver(schema) });
  const {
    formState: { errors, isSubmitting },
  } = form;

  const frequencia = form.watch('frequencia');
  const via = form.watch('via');
  const doseValor = form.watch('doseValor');

  useEffect(() => {
    if (!aberto) return;
    enviando.current = false;
    // Abre com a dose de hoje já preenchida: o ajuste quase sempre mexe em um
    // campo só, e redigitar os outros quatro é onde o erro de transcrição entra.
    form.reset({
      vigenteDe: hoje(),
      doseValor: numeroParaCampo(atual?.doseValor),
      doseUnidade: atual?.doseUnidade ?? '',
      dosePorKg: numeroParaCampo(atual?.dosePorKg),
      frequencia: atual?.frequencia ?? 'SID',
      frequenciaLivre: atual?.frequenciaLivre ?? '',
      via: atual?.via ?? '',
      motivo: '',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aberto, medicamento.id]);

  const sugerida = sugerirDosePorKg(paraNumero(doseValor ?? ''), pesoKg);

  const aoEnviar = form.handleSubmit(async (bruto) => {
    if (enviando.current) return;
    enviando.current = true;

    try {
      const dados = schema.parse(bruto);
      const dosePorKg = dados.dosePorKg ? paraNumero(dados.dosePorKg) : null;

      await registrar.mutateAsync({
        id: tratamentoId,
        medicamentoId: medicamento.id,
        data: {
          vigenteDe: dados.vigenteDe,
          doseValor: paraNumero(dados.doseValor),
          doseUnidade: dados.doseUnidade,
          dosePorKg,
          pesoKgReferencia: dosePorKg === null ? null : pesoKg,
          frequencia: dados.frequencia as RegistrarVigenciaDtoFrequencia,
          frequenciaLivre:
            dados.frequencia === 'OUTRA' ? dados.frequenciaLivre || null : null,
          via: (dados.via || null) as RegistrarVigenciaDtoVia,
          motivo: dados.motivo || null,
        },
      });

      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: getTratamentosControllerObterQueryKey(tratamentoId),
        }),
        queryClient.invalidateQueries({ queryKey: getTratamentosControllerListarQueryKey() }),
        queryClient.invalidateQueries({
          queryKey: getTratamentoVigenteControllerTratamentoVigenteQueryKey(pacienteId),
        }),
      ]);

      toast.success('Nova dose registrada. A anterior continua no histórico.');
      aoFechar();
    } catch (erro) {
      aplicarErrosDoServidor(erro, form.setError, 'Não foi possível registrar o ajuste.');
    } finally {
      enviando.current = false;
    }
  });

  return (
    <Dialog open={aberto} onOpenChange={(v) => !v && aoFechar()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Ajustar a dose de {medicamento.nome}</DialogTitle>
          <DialogDescription>
            Isto <strong>acrescenta</strong> uma dose ao histórico a partir da data escolhida. A
            dose anterior continua no histórico, com o período dela fechado — nada é
            sobrescrito.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={aoEnviar} className="space-y-4" noValidate>
          {errors.root?.message && (
            <Alert variant="destructive">
              <AlertDescription>{errors.root.message}</AlertDescription>
            </Alert>
          )}

          {atual && (
            <p className="text-sm text-muted-foreground">
              Dose de hoje: <strong>{descreverDose(atual)}</strong>{' '}
              {descreverFrequencia(atual.frequencia, atual.frequenciaLivre)} · desde{' '}
              {formatarDia(atual.vigenteDe)}
            </p>
          )}

          <Campo
            id="vigenteDe"
            rotulo="A nova dose vale a partir de"
            erro={errors.vigenteDe?.message}
            obrigatorio
          >
            <Input
              id="vigenteDe"
              type="date"
              className={CLASSE_CONTROLE}
              {...form.register('vigenteDe')}
            />
          </Campo>

          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
            <Campo id="ajuste-dose" rotulo="Dose" erro={errors.doseValor?.message} obrigatorio>
              <Input
                id="ajuste-dose"
                inputMode="decimal"
                className={CLASSE_CONTROLE}
                {...form.register('doseValor')}
              />
            </Campo>

            <Campo
              id="ajuste-unidade"
              rotulo="Unidade"
              erro={errors.doseUnidade?.message}
              obrigatorio
            >
              <Input
                id="ajuste-unidade"
                className={CLASSE_CONTROLE}
                {...form.register('doseUnidade')}
              />
            </Campo>

            <Campo
              id="ajuste-dosekg"
              rotulo="Dose/kg"
              erro={errors.dosePorKg?.message}
              dica={
                sugerida !== null && pesoKg
                  ? `Sugestão com ${pesoKg} kg: ${numeroParaCampo(sugerida)}`
                  : undefined
              }
            >
              <Input
                id="ajuste-dosekg"
                inputMode="decimal"
                className={CLASSE_CONTROLE}
                {...form.register('dosePorKg')}
              />
            </Campo>
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Campo id="ajuste-freq" rotulo="Frequência" obrigatorio>
              <Select
                value={frequencia ?? 'SID'}
                onValueChange={(v) => form.setValue('frequencia', v)}
              >
                <SelectTrigger
                  id="ajuste-freq"
                  className={CLASSE_CONTROLE}
                  aria-label="Frequência"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {FREQUENCIAS.map((f) => (
                    <SelectItem key={f} value={f}>
                      {ROTULO_FREQUENCIA[f] ?? f}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Campo>

            <Campo id="ajuste-via" rotulo="Via">
              <Select
                value={via || VIA_NENHUMA}
                onValueChange={(v) => form.setValue('via', v === VIA_NENHUMA ? '' : v)}
              >
                <SelectTrigger id="ajuste-via" className={CLASSE_CONTROLE} aria-label="Via">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={VIA_NENHUMA}>Não informada</SelectItem>
                  {VIAS.map((v) => (
                    <SelectItem key={v} value={v}>
                      {ROTULO_VIA[v] ?? v}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Campo>
          </div>

          {frequencia === 'OUTRA' && (
            <Campo
              id="ajuste-freqlivre"
              rotulo="Qual frequência?"
              erro={errors.frequenciaLivre?.message}
            >
              <Input
                id="ajuste-freqlivre"
                className={CLASSE_CONTROLE}
                {...form.register('frequenciaLivre')}
              />
            </Campo>
          )}

          <Campo
            id="ajuste-motivo"
            rotulo="Motivo"
            erro={errors.motivo?.message}
            dica="Opcional — por que a dose mudou. Fica junto da vigência, para sempre."
          >
            <Input
              id="ajuste-motivo"
              className={CLASSE_CONTROLE}
              placeholder="Ex.: cortisol pós-ACTH acima do alvo"
              {...form.register('motivo')}
            />
          </Campo>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={aoFechar}>
              Cancelar
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Registrar nova dose
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
